import StdForm from '@shared/components/forms/auto-form/std-form/std-form.vue';
import { Component, Prop } from 'vue-property-decorator';
import EntityTableProvider from '@shared/components/tables/entity-table-provider/entity-table-provider.vue';
import EntityTable from '@shared/components/tables/entity-table/entity-table.vue';
import BpcPageTitle from '@shared/layouts/components/page-title/page-title.vue';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import EntityDeleteModalProvider from '@shared/components/modals/entity-delete-modal-provider/entity-delete-modal-provider.vue';
import ConfirmationModal from '@shared/components/modals/confirmation-modal/confirmation-modal.vue';
import { ValidationObserver } from 'vee-validate';
import { mixins } from 'vue-class-component';
import AbstractTable from '@shared/components/tables/abstract-table/abstract-table';
import StdFilter from '@shared/components/filters/std-filter/std-filter.vue';
import { library } from '@fortawesome/fontawesome-svg-core';
import { faSync } from '@fortawesome/free-solid-svg-icons';
import { Account } from '@modules/accounting/interfaces/account';
import DrawerModal from '@shared/components/modals/drawer-modal/drawer-modal.vue';
import AuthorizationHelper from '@auth/services/authorization.helper';
import {
  AccountingIntegrationSettings,
  SyncServiceType,
} from '@accounting-integrations/interfaces/accounting-integrations';
import { CenterSetting } from '@core/interfaces/center-setting';

library.add(faSync);

@Component({
  name: 'bpc-invoice-sync-item-table',
  components: {
    'bpc-entity-table-provider': EntityTableProvider,
    'bpc-entity-table': EntityTable,
    'bpc-page-title': BpcPageTitle,
    'font-awesome-icon': FontAwesomeIcon,
    'bpc-entity-delete-modal-provider': EntityDeleteModalProvider,
    'bpc-confirmation-modal': ConfirmationModal,
    'bpc-std-form': StdForm,
    'bpc-std-filter': StdFilter,
    ValidationObserver,
    'bpc-drawer-modal': DrawerModal,
  },
})
export default class InvoiceSyncItemTable extends mixins(AbstractTable) {
  // PROPS
  @Prop(String) readonly title: string;
  @Prop(String) readonly subtitle: string;

  // DATA
  public entity: Account = null;
  public entityName = 'invoice-sync-item';
  public pluralEntityName = 'invoice-sync-items';
  public showDrawerSettingSection: boolean = false;
  public showSettingForm: boolean = false;
  public permissionEntityNameToUse = 'invoice_sync_items';
  public selectedRowEntity = null;

  public flagsToCheckBeforeLoadEntities: string[] = [
    'configLoaded',
    'filtersLoaded',
    'paginationLoaded',
    'orderLoaded',
    'urlParametersLoaded',
  ];

  public customRowActions = {
    process: {
      activated: false,
      type: 'action',
      icon: 'cloud-upload-alt',
      show: false,
      event: 'process',
      text: 'process',
      permissions: ['process'],
    },
    download: {
      activated: false,
      type: 'action',
      icon: 'cloud-download-alt',
      show: false,
      event: 'download',
      text: 'download_sync',
      permissions: ['download'],
    },
  };

  public customPageActions = {
    bulkProcess: {
      activated: false,
      type: 'action',
      icon: 'cloud-upload-alt', //'times',
      variant: 'info',
      text: 'process',
      show: false,
      event: 'bulk-process',
      selectElements: true,
      tag: 'left',
      permissions: ['bulk_process'],
    },
    bulkDownload: {
      activated: false,
      type: 'action',
      icon: 'cloud-download-alt', //'times',
      variant: 'info',
      text: 'download_sync',
      show: false,
      event: 'bulk-download',
      selectElements: true,
      tag: 'left',
      permissions: ['bulk_process'],
    },
  };

  // COMPUTED
  get centerSetting(): CenterSetting {
    return this.$store.getters['centerSettings/centerSettings'];
  }

  get accountingIntegrationSettings(): AccountingIntegrationSettings {
    return this.centerSetting?.accounting_integration_settings;
  }

  get syncServiceType(): SyncServiceType {
    return this.accountingIntegrationSettings?.sync_service_type;
  }

  get processModalTitle() {
    return this.$t('global.modals.bulk-process.title', {
      entityName: this.$t('global.models.' + this.entityName + '.name'),
      entityPluralName: this.$t('global.models.' + this.entityName + '.pluralName'),
      thisEntityName: this.$t('global.models.' + this.entityName + '.thisName'),
      thisEntityPluralName: this.$t('global.models.' + this.entityName + '.thisPluralName'),
    });
  }

  get processModalBody() {
    return this.$t('global.modals.bulk-process.body', {
      entityName: this.$t('global.models.' + this.entityName + '.name'),
      entityPluralName: this.$t('global.models.' + this.entityName + '.pluralName'),
      thisEntityName: this.$t('global.models.' + this.entityName + '.thisName'),
      thisEntityPluralName: this.$t('global.models.' + this.entityName + '.thisPluralName'),
    });
  }

  // METHODS
  onShow(e) {
    this.onEdit(e);
  }

  customOnRowAction($event: { event: string; item: any }) {
    switch ($event.event) {
      case 'process':
        this.openProcessRowModal($event.item);
        break;
      case 'download':
        this.downloadInvoiceSyncItemFile($event.item);
        break;
    }
  }

  downloadInvoiceSyncItemFile(item) {
    const downloadUrl = item?.item?.bob_link_zip?.url;
    window.location.href = downloadUrl;
  }

  extendUpdateRowActions(entity: any) {
    entity.actions.process.show =
      entity.status !== 'blocked' &&
      entity?.sync_service !== 'bob_link' &&
      entity.status !== 'synced' &&
      entity.status !== 'processing' &&
      this.$refs.provider &&
      this.$refs.provider.queryConfig.params.status !== 'trash' &&
      !this.$refs.provider.queryConfig.params.onlyTrashed &&
      this.$authorizationService.hasPermission('manage_invoice_sync_items');

    entity.actions.download.show =
      entity?.sync_service === 'bob_link' &&
      entity?.status === 'synced' &&
      entity?.bob_link_zip?.url &&
      this.$authorizationService.hasPermission('manage_invoice_sync_items');
  }

  customOnPageAction($event: string) {
    switch ($event) {
      case 'bulk-process':
        this.openProcessModal();
        break;
      case 'bulk-download':
        this.onBulkDownload();
        break;
    }
  }

  onBulkDownload() {
    const basePath = this.$bpcApiRouteHelper.getEntityPath('invoice-sync-items');
    const invoiceSyncItems = this.getSelectedInvoiceSyncItemIds();
    if (invoiceSyncItems.length) {
      const fileName = `invoice-sync-${Date.now()}.zip`;
      this.$bpcApi
        .postDownload(`${basePath}/bulk-download`, fileName, {
          invoice_sync_item_ids: invoiceSyncItems,
        })
        .subscribe(
          res => {
            console.log(res);
          },
          error => {
            this.$bpcMessage.showErrorMessage({
              text: 'global.invoice_sync_item.bulk_download.error',
            });
          },
        );
    } else {
      // TODO display modal error
    }
  }

  getSelectedInvoiceSyncItemIds() {
    const items = this.$refs?.provider?.items || [];
    return items
      .filter((item: { checked: boolean; id: number }) => item.checked)
      .map((item: { checked: boolean; id: number }) => item.id);
  }

  openProcessRowModal(item) {
    this.selectedRowEntity = item.item;
    this.$bvModal.show('modal-process-invoice-sync-items');
  }

  openProcessModal() {
    this.$bvModal.show('modal-process-invoice-sync-items');
  }

  closeProcessModal() {
    this.selectedRowEntity = null;

    this.$bvModal.hide('modal-process-invoice-sync-items');
  }

  onConfirmProcessRowModal() {
    const ids = [this.selectedRowEntity.id];
    this.onConfirmProcessModal(ids);
  }

  onConfirmProcessModal(ids = null) {
    const basePath = this.$bpcApiRouteHelper.getEntityPath('invoice-sync-items');
    const sessionCode = `invoice-sync-item-table-${Date.now()}`;

    if (this.syncServiceType === 'file') {
      this.listenInvoiceSyncItemDownloadEvent(sessionCode);
    }

    this.$bpcApi
      .post(`${basePath}/synchronize`, {
        ids: ids || this.selection,
        session_code: sessionCode,
      })
      .subscribe(
        res => {
          this.$refs.provider.getEntities();
          this.closeProcessModal();
        },
        error => {
          this.closeProcessModal();
        },
      );
  }

  listenInvoiceSyncItemDownloadEvent(sessionCode) {
    this.$bpcEcho
      .getInvoiceSyncChannel(sessionCode)
      .listen('.BPC\\AccountingIntegrations\\Events\\InvoiceSyncFileGenerated', data => {
        this.downloadInvoiceSyncItemGeneratedFile(sessionCode, data);
      });
  }

  downloadInvoiceSyncItemGeneratedFile(sessionCode, data) {
    const downloadUrl = data?.fileUrl;
    window.location.href = downloadUrl;
    this.$bpcEcho.leaveInvoiceSyncChannel(sessionCode);
  }

  extendUpdatePageActions() {
    if (this.config) {
      for (const activatedPageAction of this.config.data.activatedPageActions) {
        if (this.customPageActions[activatedPageAction]) {
          this.customPageActions[activatedPageAction].activated = true;
        }
      }
    }

    this.customPageActions.bulkProcess.show =
      this.$refs.provider &&
      this.syncServiceType === 'api' &&
      this.$refs.provider.queryConfig.params.status !== 'trash' &&
      !this.$refs.provider.queryConfig.params.onlyTrashed;

    this.customPageActions.bulkDownload.show =
      this.$refs.provider &&
      this.syncServiceType === 'file' &&
      this.$refs.provider.queryConfig.params.status !== 'trash' &&
      !this.$refs.provider.queryConfig.params.onlyTrashed;

    // Check Permissions
    for (const activatedPageAction of this.config.data.activatedPageActions) {
      if (
        this.customPageActions[activatedPageAction] &&
        this.customPageActions[activatedPageAction]['permissions']
      ) {
        const permissions = [];
        this.customPageActions[activatedPageAction]['permissions'].forEach(permissionName => {
          permissions.push(
            {
              name: AuthorizationHelper.getPermissionName(
                permissionName,
                (this.permissionEntityNameToUse
                  ? this.permissionEntityNameToUse
                  : this.pluralEntityName
                ).replace('-', '_'),
              ),
            },
            {
              name: AuthorizationHelper.getPermissionName(
                permissionName,
                (this.permissionEntityNameToUse
                  ? this.permissionEntityNameToUse
                  : this.pluralEntityName
                ).replace('-', '_'),
                true,
              ),
            },
          );
        });
        this.customPageActions[activatedPageAction][
          'activated'
        ] = this.$authorizationService.hasPermissions(permissions);
      }
    }
  }

  /* Lifecycle hooks */

  // created() {}
  // mounted() {}
  // updated() {}
  // destroyed() {}
}
